import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yeshuman/sdk'
import { useEffect, useState } from 'react'
import {
  type TherapeuticItem,
  type TherapeuticTypeFilter,
  type CreateTherapeuticData,
  type UpdateTherapeuticData,
} from '../hooks/use-therapeutics'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface TherapeuticFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  therapeutic?: TherapeuticItem | null
  defaultType?: TherapeuticTypeFilter
  onCreate: (data: CreateTherapeuticData) => Promise<void>
  onUpdate: (data: UpdateTherapeuticData) => Promise<void>
}

function localInputFromIso(iso: string): string {
  const date = new Date(iso)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

export function TherapeuticFormDialog({
  open,
  onOpenChange,
  therapeutic,
  defaultType = 'medication',
  onCreate,
  onUpdate,
}: TherapeuticFormDialogProps) {
  const [therapeuticType, setTherapeuticType] = useState<TherapeuticTypeFilter>(defaultType)
  const [recordedAt, setRecordedAt] = useState('')
  const [notes, setNotes] = useState('')
  const [name, setName] = useState('')
  const [doseText, setDoseText] = useState('')
  const [frequencyText, setFrequencyText] = useState('')
  const [label, setLabel] = useState('')
  const [durationMinutes, setDurationMinutes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    if (therapeutic) {
      const t = therapeutic.therapeutic_type
      if (t === 'medication' || t === 'supplement' || t === 'therapy_session') {
        setTherapeuticType(t)
      }
      setRecordedAt(localInputFromIso(therapeutic.recorded_at))
      setNotes(therapeutic.notes || '')
      setName(therapeutic.name || '')
      setDoseText(therapeutic.dose_text || '')
      setFrequencyText(therapeutic.frequency_text || '')
      setLabel(therapeutic.label || '')
      setDurationMinutes(
        therapeutic.duration_minutes != null ? String(therapeutic.duration_minutes) : '',
      )
    } else {
      const now = new Date()
      setRecordedAt(new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16))
      setTherapeuticType(defaultType)
      setNotes('')
      setName('')
      setDoseText('')
      setFrequencyText('')
      setLabel('')
      setDurationMinutes('')
    }
  }, [therapeutic, open, defaultType])

  const toIso = (localDt: string) => new Date(localDt).toISOString()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const isoRecorded = recordedAt ? toIso(recordedAt) : undefined

      if (therapeutic) {
        const update: UpdateTherapeuticData = {
          therapeutic_id: therapeutic.id,
          recorded_at: isoRecorded,
          notes,
        }
        if (therapeutic.therapeutic_type === 'medication') {
          if (!name.trim()) {
            toast.error('Name is required')
            setIsSubmitting(false)
            return
          }
          update.name = name.trim()
          update.dose_text = doseText
          update.frequency_text = frequencyText
        } else if (therapeutic.therapeutic_type === 'supplement') {
          if (!name.trim()) {
            toast.error('Name is required')
            setIsSubmitting(false)
            return
          }
          update.name = name.trim()
          update.dose_text = doseText
        } else if (therapeutic.therapeutic_type === 'therapy_session') {
          if (!label.trim()) {
            toast.error('Label is required')
            setIsSubmitting(false)
            return
          }
          update.label = label.trim()
          if (durationMinutes.trim()) {
            const n = parseInt(durationMinutes, 10)
            if (!Number.isNaN(n)) update.duration_minutes = n
          } else {
            update.duration_minutes = null
          }
        }
        await onUpdate(update)
        toast.success('Entry updated')
      } else {
        const create: CreateTherapeuticData = {
          therapeutic_type: therapeuticType,
          recorded_at: isoRecorded,
          notes: notes || undefined,
        }
        if (therapeuticType === 'medication') {
          if (!name.trim()) {
            toast.error('Name is required')
            setIsSubmitting(false)
            return
          }
          create.name = name.trim()
          create.dose_text = doseText || undefined
          create.frequency_text = frequencyText || undefined
        } else if (therapeuticType === 'supplement') {
          if (!name.trim()) {
            toast.error('Name is required')
            setIsSubmitting(false)
            return
          }
          create.name = name.trim()
          create.dose_text = doseText || undefined
        } else {
          if (!label.trim()) {
            toast.error('Label is required')
            setIsSubmitting(false)
            return
          }
          create.label = label.trim()
          if (durationMinutes.trim()) {
            const n = parseInt(durationMinutes, 10)
            if (!Number.isNaN(n)) create.duration_minutes = n
          }
        }
        await onCreate(create)
        toast.success('Entry saved')
      }
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setIsSubmitting(false)
    }
  }

  const effectiveType = therapeutic ? therapeutic.therapeutic_type : therapeuticType

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{therapeutic ? 'Edit entry' : 'Log therapeutic'}</DialogTitle>
          <DialogDescription>
            Self-reported wellness journaling — not medical prescribing. Confirm details when using photos or OCR.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!therapeutic ? (
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={therapeuticType}
                onValueChange={(v) => setTherapeuticType(v as TherapeuticTypeFilter)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medication">Medication</SelectItem>
                  <SelectItem value="supplement">Supplement</SelectItem>
                  <SelectItem value="therapy_session">Therapy session</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="therapeutic-when">When</Label>
            <Input
              id="therapeutic-when"
              type="datetime-local"
              value={recordedAt}
              onChange={(e) => setRecordedAt(e.target.value)}
              required
            />
          </div>

          {(effectiveType === 'medication' || effectiveType === 'supplement') && (
            <>
              <div className="space-y-2">
                <Label htmlFor="therapeutic-name">Name</Label>
                <Input id="therapeutic-name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="therapeutic-dose">Dose (optional)</Label>
                <Input
                  id="therapeutic-dose"
                  value={doseText}
                  onChange={(e) => setDoseText(e.target.value)}
                  placeholder="e.g. 500 mg"
                  className="placeholder:italic"
                />
              </div>
              {effectiveType === 'medication' ? (
                <div className="space-y-2">
                  <Label htmlFor="therapeutic-frequency">Frequency (optional)</Label>
                  <Input
                    id="therapeutic-frequency"
                    value={frequencyText}
                    onChange={(e) => setFrequencyText(e.target.value)}
                    placeholder="e.g. Twice daily"
                    className="placeholder:italic"
                  />
                </div>
              ) : null}
            </>
          )}

          {effectiveType === 'therapy_session' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="therapeutic-label">Session label</Label>
                <Input id="therapeutic-label" value={label} onChange={(e) => setLabel(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="therapeutic-duration">Duration (minutes, optional)</Label>
                <Input
                  id="therapeutic-duration"
                  type="number"
                  min={1}
                  max={1440}
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="therapeutic-notes">Notes</Label>
            <Textarea
              id="therapeutic-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="placeholder:italic"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
